/**
 * Повторный вход поверх смонтированной работы (контракты §5, E1.4).
 *
 * Когда сервер перестаёт принимать сессию, страница и её незавершённые формы остаются
 * смонтированными, но недоступными: они под непрозрачным окном входа и помечены inert,
 * а клиент API не отправляет запросы (src/api/client.ts). После входа:
 * - тот же пользователь возвращается к форме; права берутся из нового ответа сервера,
 *   и маршрут, на который права больше нет, снимает форму в том же обновлении, в котором
 *   закрывается окно;
 * - другой пользователь получает рабочее место заново: поддерево перемонтируется по
 *   идентичности сессии, черновики и кеш прежнего пользователя очищают привязки
 *   bindDraftsToSessionIdentity и bindCacheToSessionIdentity.
 * Исходная мутация после входа сама не повторяется.
 */

import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { signIn } from '@/api/auth';
import { sessionIdentity, useSessionStore } from '@/stores/session';
import { errorMessage, messageText } from '@/utils/displayMessage';

export function ReauthenticationBoundary({ children }: { children: ReactNode }) {
	const authState = useSessionStore((s) => s.authState);
	const user = useSessionStore((s) => s.user);
	const queryClient = useQueryClient();
	const locked = authState === 'reauth_required';

	useEffect(() => {
		if (locked) {
			void queryClient.cancelQueries();
		}
	}, [locked, queryClient]);

	return (
		<>
			<div className="contents" inert={locked} key={sessionIdentity(user) ?? 'anonymous'}>
				{children}
			</div>
			{locked && user && <ReauthenticationDialog email={user.email} />}
		</>
	);
}

function ReauthenticationDialog({ email }: { email: string }) {
	const { t } = useTranslation('common');
	const queryClient = useQueryClient();
	const [login, setLogin] = useState(email);
	const [password, setPassword] = useState('');
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const loginId = useId();
	const passwordId = useId();

	async function submit(event: FormEvent) {
		event.preventDefault();
		setPending(true);
		setError(null);
		try {
			const session = await signIn(login.trim(), password);
			const sameIdentity =
				sessionIdentity(session.user) === sessionIdentity(useSessionStore.getState().user);
			useSessionStore.getState().setSession(session.user, session.token);
			if (sameIdentity) {
				void queryClient.invalidateQueries();
			}
		} catch (err) {
			setError(messageText(t, errorMessage(err, { messageKey: 'common:reauth.failed' })));
			setPending(false);
		}
	}

	return (
		<DialogPrimitive.Root open>
			<DialogPrimitive.Portal>
				{/* Непрозрачная подложка: под окном может остаться форма с секретом */}
				<DialogPrimitive.Overlay className="ws-workspace-dialog-backdrop fixed inset-0 z-[var(--z-modal)] bg-[var(--ws-bg)]" />
				<DialogPrimitive.Content
					className="ws-workspace-dialog fixed top-1/2 left-1/2 z-[var(--z-modal)] -translate-x-1/2 -translate-y-1/2 overflow-auto bg-[var(--ws-surface)]"
					onEscapeKeyDown={(event) => event.preventDefault()}
					onPointerDownOutside={(event) => event.preventDefault()}
					onInteractOutside={(event) => event.preventDefault()}
				>
					<div className="ws-workspace-dialog-head">
						<div>
							<p className="ws-heading-eyebrow">{t('reauth.eyebrow')}</p>
							<DialogPrimitive.Title asChild>
								<h2>{t('reauth.title')}</h2>
							</DialogPrimitive.Title>
						</div>
					</div>
					<form className="ws-workspace-dialog-body" onSubmit={submit}>
						<DialogPrimitive.Description className="ws-muted">
							{t('reauth.description')}
						</DialogPrimitive.Description>
						<label className="ws-form-field" htmlFor={loginId}>
							<span>{t('reauth.email')}</span>
							<input
								id={loginId}
								type="email"
								autoComplete="username"
								value={login}
								onChange={(event) => setLogin(event.target.value)}
								required
							/>
						</label>
						<label className="ws-form-field" htmlFor={passwordId}>
							<span>{t('reauth.password')}</span>
							<input
								id={passwordId}
								type="password"
								autoComplete="current-password"
								value={password}
								onChange={(event) => setPassword(event.target.value)}
								required
								autoFocus
							/>
						</label>
						{error && (
							<p role="alert" className="ws-field-hint text-[var(--ws-red)]">
								{error}
							</p>
						)}
						<div className="ws-heading-actions">
							<button
								type="button"
								className="ws-btn ws-subtle"
								onClick={() => {
									queryClient.clear();
									useSessionStore.getState().clearSession();
								}}
							>
								{t('reauth.signOut')}
							</button>
							<button type="submit" className="ws-btn ws-primary" disabled={pending}>
								{t('reauth.submit')}
							</button>
						</div>
					</form>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
